import RecoverableImage from './Media/RecoverableImage';
import { urlMetaImageRecovery } from '../util/imageRecovery';
import { getUrlDisplayTitle } from '../util/displayText';
import UrlPreviewReadButton from './UrlPreviewReadButton';
import UrlPreviewSource from './UrlPreviewSource';
import '../i18n';
import {useTranslation} from 'react-i18next';
import React from 'react';

import Markdown from 'markdown-to-jsx';
import { FiBookOpen, FiPlay } from 'react-icons/fi';

import { FILE_BLUE, PRIMARY_COLOR_1 } from '../appearance/Colors';
import { getUrlMetaVideoSource, isUrlMetaVideo } from '../util/url-meta-media';
import { getReadableArticleContent } from './ArticleReaderModal';
import {
    hasUrlPreviewMarkdownFormatting,
    normalizeUrlPreviewText,
    truncateUrlPreviewText
} from '../util/urlPreviewText';

const DARK_THEME = {
    background: 'rgba(255, 255, 255, 0.035)',
    border: 'rgba(255, 255, 255, 0.18)',
    label: 'rgba(255, 255, 255, 0.62)',
    title: 'rgba(255, 255, 255, 0.92)',
    meta: 'rgba(255, 255, 255, 0.74)',
    imageBackground: 'rgba(255, 255, 255, 0.05)'
};

const LIGHT_THEME = {
    background: PRIMARY_COLOR_1,
    border: 'rgba(0, 0, 0, 0.08)',
    label: 'rgba(0, 0, 0, 0.52)',
    title: 'rgba(0, 0, 0, 0.82)',
    meta: 'rgba(0, 0, 0, 0.58)',
    imageBackground: 'rgba(0, 0, 0, 0.04)'
};

const markdownOptions = {
    forceBlock: false,
    overrides: {
        a: {
            component: 'span',
            props: {
                style: {
                    textDecoration: 'none',
                    cursor: 'pointer',
                    wordBreak: 'normal',
                    overflowWrap: 'anywhere',
                    display: 'inline',
                    whiteSpace: 'normal',
                    fontSize: '14px',
                    lineHeight: '20px',
                    fontWeight: 400
                },
                onClick: e => {
                    e.stopPropagation();
                    if (e.target.href) {
                        window.open(e.target.href, '_blank', 'noreferrer');
                    }
                }
            }
        },
        p: {
            props: {
                style: {
                    margin: 0,
                    fontSize: 'inherit',
                    lineHeight: 'inherit',
                    color: 'inherit'
                }
            }
        }
    }
};

function getQuotedPreviewImage(metaData) {
    const gallery = Array.isArray(metaData.gallery) ? metaData.gallery : [];
    const galleryImages = Array.isArray(metaData.gallery_images) ? metaData.gallery_images : [];

    const firstGalleryImage = gallery.find(item => typeof item?.image === 'string' && item.image.trim())?.image;
    const firstGalleryImageUrl = galleryImages.find(image => typeof image === 'string' && image.trim());

    return metaData.image_cdn_url || metaData.image || firstGalleryImage || firstGalleryImageUrl || null;
}

function UrlMetaQuotedPreview({ metaData, showOpen = true, expandable = false, mediaAboveText = false, variant = 'light', allowImageRefresh = false, onReadArticle, onOpenWorkspace }) {
    const {t: lnT} = useTranslation();
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [isTextClipped, setIsTextClipped] = React.useState(false);
    const textRef = React.useRef(null);
    React.useEffect(() => setIsExpanded(false), [metaData?.url, metaData?.title, metaData?.description]);
    React.useLayoutEffect(() => {
        const node = textRef.current;
        if (!expandable || !node) {
            setIsTextClipped(false);
            return undefined;
        }
        if (isExpanded) return undefined;
        const measure = () => setIsTextClipped(
            node.scrollHeight > node.clientHeight + 1 || node.scrollWidth > node.clientWidth + 1
        );
        measure();
        const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
        observer?.observe(node);
        window.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [expandable, isExpanded, metaData?.url, metaData?.title, metaData?.description]);

    if (!metaData || typeof metaData !== 'object') {
        return null;
    }

    const theme = variant === 'dark' ? DARK_THEME : LIGHT_THEME;
    const themedMarkdownOptions = {
        ...markdownOptions,
        overrides: {
            ...markdownOptions.overrides,
            blockquote: {
                component: 'div',
                props: {
                    style: {
                        margin: '4px 0',
                        borderLeft: `3px solid ${FILE_BLUE}`,
                        paddingLeft: '8px',
                        fontStyle: 'italic',
                        fontSize: 14,
                        lineHeight: '20px',
                        color: theme.title
                    }
                }
            }
        }
    };
    const normalizedTitle = normalizeUrlPreviewText(getUrlDisplayTitle(metaData.title || ''));
    const normalizedDescription = normalizeUrlPreviewText(metaData.description || '');
    const displayText = normalizedTitle || normalizedDescription;
    const usesMarkdown = hasUrlPreviewMarkdownFormatting(displayText);
    const truncatedText = truncateUrlPreviewText(displayText.replace(/\s+/g, ' ').trim(), 160);
    const shownText = expandable && isExpanded ? displayText : truncatedText;
    const canExpand = expandable && (isExpanded || isTextClipped || displayText.length > 160 || displayText.split('\n').length > 3);
    const authorLabel = String(metaData.author || '').trim();
    const authorImage = metaData.author_image_cdn_url || metaData.author_image || null;
    const imageSource = getQuotedPreviewImage(metaData);
    const videoSource = getUrlMetaVideoSource(metaData);
    const isVideo = isUrlMetaVideo(metaData);
    // Keep this in sync with the parent URL preview. ArticleReaderModal does
    // not render when its cleanup removes all content (for example, an
    // extraction that contains only the duplicated title). Showing Read for
    // that payload made the quoted-card action appear to do nothing.
    const hasReadableArticleBody = getReadableArticleContent(
        metaData.article_body,
        metaData.title
    ).length > 0;

    if (!displayText && !metaData.url && !imageSource && !videoSource) {
        return null;
    }

    const hasMedia = Boolean(imageSource || videoSource);
    const hasThumbnail = hasMedia;

    return (
        <>
        <div
            data-message-interactive="true"
            style={{
                marginTop: 10,
                padding: 10,
                borderRadius: 12,
                border: `1px solid ${theme.border}`,
                background: theme.background
            }}
        >
            <div style={{ display: mediaAboveText ? 'flex' : 'grid', flexDirection: 'column', gridTemplateColumns: hasThumbnail ? 'minmax(0, 1fr) min(28%, 96px)' : 'minmax(0, 1fr)', gap: mediaAboveText ? 12 : 8, minWidth: 0, alignItems: 'start' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                    {authorLabel ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: displayText ? 6 : 0 }}>
                            {authorImage ? (
                                <RecoverableImage
                                    sources={urlMetaImageRecovery(metaData, true).sources} refresh={urlMetaImageRecovery(metaData, true).refresh} allowRefresh={allowImageRefresh}
                                    src={authorImage}
                                    alt=""
                                    referrerPolicy="no-referrer"
                                    style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                                />
                            ) : null}
                            <div style={{ color: theme.meta, fontSize: 13, fontWeight: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                                {authorLabel}
                            </div>
                        </div>
                    ) : null}
                    {displayText ? (
                        <div
                            ref={textRef}
                            style={{
                                color: theme.title,
                                fontSize: 14,
                                lineHeight: '20px',
                                whiteSpace: usesMarkdown ? 'normal' : 'pre-wrap',
                                overflowWrap: 'anywhere',
                                display: expandable && isExpanded ? 'block' : '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: expandable && isExpanded ? undefined : 3,
                                overflow: 'hidden'
                            }}
                        >
                            {usesMarkdown ? (
                                <Markdown options={themedMarkdownOptions}>
                                    {shownText}
                                </Markdown>
                            ) : (
                                shownText
                            )}
                        </div>
                    ) : null}
                    {canExpand ? (
                        <button
                            type="button"
                            onClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                setIsExpanded(previous => !previous);
                            }}
                            onMouseDown={event => event.stopPropagation()}
                            onPointerDown={event => event.stopPropagation()}
                            style={{ marginTop: 6, padding: 0, border: 0, background: 'transparent', color: FILE_BLUE, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >{lnT(isExpanded ? 'messages:showLess' : 'messages:showMore')}</button>
                    ) : null}
                </div>
                {hasMedia ? (
                    <div style={{ width: '100%', maxWidth: mediaAboveText ? 320 : undefined, order: mediaAboveText ? -1 : undefined, minWidth: 0, position: 'relative' }}>
                        {isVideo && videoSource ? (
                            <button
                                type="button"
                                aria-label={lnT('common:open')}
                                onClick={event => {
                                    event.stopPropagation();
                                    onOpenWorkspace?.({ type: 'video', src: videoSource, originalUrl: metaData.url, poster: imageSource });
                                }}
                                style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: mediaAboveText ? 'auto' : 72, aspectRatio: mediaAboveText ? '16 / 9' : undefined, maxHeight: mediaAboveText ? 250 : undefined, padding: 0, border: 0, borderRadius: 8, overflow: 'hidden', background: '#152329', color: '#fff', cursor: 'pointer' }}
                            >
                                {imageSource ? <RecoverableImage sources={urlMetaImageRecovery(metaData).sources} refresh={urlMetaImageRecovery(metaData).refresh} allowRefresh={allowImageRefresh} src={imageSource} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                                <span style={{ position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', background: '#22363d' }}>
                                    <FiPlay size={14} />
                                </span>
                            </button>
                        ) : imageSource ? (
                            <button
                                type="button"
                                onClick={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onOpenWorkspace?.({
                                        type: 'image',
                                        image: { url: imageSource, cdn_url: imageSource },
                                        url: imageSource,
                                        originalUrl: metaData.url || imageSource
                                    });
                                }}
                                style={{ display: 'block', width: '100%', padding: 0, border: 0, borderRadius: 12, background: 'transparent', cursor: onOpenWorkspace ? 'pointer' : 'default' }}
                            >
                                <RecoverableImage
                                    sources={urlMetaImageRecovery(metaData).sources} refresh={urlMetaImageRecovery(metaData).refresh} allowRefresh={allowImageRefresh}
                                    src={imageSource}
                                    alt={normalizedTitle || authorLabel || lnT('common:quotedPreview')}
                                    referrerPolicy="no-referrer"
                                    style={{
                                        width: '100%',
                                        height: mediaAboveText ? 'auto' : 72,
                                        maxHeight: mediaAboveText ? 250 : undefined,
                                        borderRadius: 8,
                                        objectFit: mediaAboveText ? 'contain' : 'cover',
                                        display: 'block',
                                        background: theme.imageBackground
                                    }}
                                />
                            </button>
                        ) : null}
                    </div>
                ) : null}
            </div>
                    {hasReadableArticleBody && typeof onReadArticle === 'function' ? (
                        <UrlPreviewReadButton
                            $isLight={variant !== 'dark'}
                            type="button"
                            onClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                onReadArticle(metaData);
                            }}
                            onMouseDown={event => event.stopPropagation()}
                            onPointerDown={event => event.stopPropagation()}
                            style={{ marginTop: 8 }}
                        >
                            <FiBookOpen size={14} />{lnT('messages:read')}</UrlPreviewReadButton>
                    ) : null}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, marginTop: 8 }}>
                <UrlPreviewSource metaData={metaData} color={variant === 'dark' ? undefined : theme.meta} />

                    {showOpen && metaData.url ? (
                        <a
                            href={metaData.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={event => {
                                event.stopPropagation();
                            }}
                            onMouseDown={event => event.stopPropagation()}
                            onPointerDown={event => event.stopPropagation()}
                            style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, padding: 0, border: 0, background: 'transparent', cursor: 'pointer', textDecoration: 'none', color: FILE_BLUE, fontSize: 14, fontWeight: 600 }}
                        >{lnT('common:open')}</a>
                    ) : null}
            </div>
        </div>
        </>
    );
}

export default UrlMetaQuotedPreview;
